import { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { Input } from './Input';
import { formatDate } from '../lib/format';
import { recordCashPayment, CASH_METHOD_LABEL, type CashMethod } from '../api/billing';

/**
 * Record an off-gateway payment (cash / bKash / Nagad / bank transfer) against a
 * business. Shared by the reseller CustomerDetail + admin BusinessDetail "Record
 * payment" actions. Calls POST /payments/cash (extends the subscription, writes a
 * paid invoice, and the correct reseller_ledger entry for reseller-collected money).
 * The parent supplies `onRecorded` to invalidate its own queries.
 */
export function CashPaymentModal({
  open,
  onClose,
  businessId,
  businessName,
  onRecorded,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  onRecorded?: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<CashMethod>('manual_cash');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ invoiceNo: string; currentPeriodEnd: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const amountNum = Number(amount) || 0;
  const valid = amountNum > 0;

  function close() {
    onClose();
    setTimeout(() => {
      setAmount('');
      setMethod('manual_cash');
      setResult(null);
      setError(null);
    }, 250);
  }

  async function save() {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await recordCashPayment({ businessId, amountMinor: Math.round(amountNum * 100), method });
      onRecorded?.();
      setResult({ invoiceNo: res.invoiceNo, currentPeriodEnd: res.currentPeriodEnd });
    } catch (e) {
      const code = (e as { status?: number })?.status;
      setError(code === 403 ? 'You are not allowed to record this payment.' : 'Could not record the payment. Check the amount and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? 'Payment recorded' : `Record payment · ${businessName}`}
      size="md"
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button onClick={save} loading={submitting} disabled={!valid}>Record payment</Button>
          </>
        )
      }
    >
      {result ? (
        <div className="text-center py-6">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <CheckCircle className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Payment recorded</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Invoice <span className="mono">{result.invoiceNo}</span> · subscription active
            {result.currentPeriodEnd ? <> until <span className="tabular-nums">{formatDate(result.currentPeriodEnd)}</span></> : ''}.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
          )}
          <Input
            label="Amount (BDT)"
            required
            type="number"
            min={0}
            prefix={<span className="text-sm">৳</span>}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as CashMethod)}
              className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
            >
              {(Object.keys(CASH_METHOD_LABEL) as CashMethod[]).map((m) => (
                <option key={m} value={m}>{CASH_METHOD_LABEL[m]}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Records an off-gateway payment and extends the subscription. For reseller-collected money the platform commission is settled automatically on the reseller ledger.
          </p>
        </div>
      )}
    </Modal>
  );
}
